import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/utils/useAuth';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { ROUTE_PATH } from '../../routes/route-paths';
import { ArrowRight, Hamburger, HamburgerClose, CalendarIcon } from '../../assets/images/svgs';
import { domainLogo, domainLogoAlt } from '../domain-logo';
import { CallAListenerIcon } from '../icons/call-a-listener-icon';
import { useSettingsModal, SettingsPage } from '../../contexts/settings-modal-context';
import { SupportModal } from '../../containers/support/support-modal';
import { LogoutModal } from '../modals/logout-modal/logout-modal';
import Toast from '../common/pop-up-message';
import { useCallRequests, useActiveCalls } from '../../hooks/api/useCalls';
import { usePostCalls } from '../../hooks/api/usePostCall';
import { CallDetailsModal } from '../modals/call-details-modal/call-details-modal';
import { HowCallWorksModal } from '../modals/how-call-works-modal';
import { PostCallModal } from '../post-call-form/post-call-modal';
import { useTagGroups } from '../../hooks/api/useTagGroups';

// Chat bubble icon for "How'd it go?" button
const ChatBubbleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 7.66669C14.0023 8.5466 13.7967 9.41461 13.4 10.2C12.9296 11.1412 12.2065 11.9328 11.3116 12.4862C10.4168 13.0396 9.3855 13.3329 8.33333 13.3334C7.45342 13.3356 6.58541 13.1301 5.8 12.7334L2 14L3.26667 10.2C2.86995 9.41461 2.66437 8.5466 2.66667 7.66669C2.66707 6.61452 2.96041 5.58325 3.51381 4.68839C4.06722 3.79352 4.85884 3.0704 5.8 2.60002C6.58541 2.2033 7.45342 1.99772 8.33333 2.00002H8.66667C10.0562 2.07668 11.3687 2.66319 12.3528 3.64726C13.3368 4.63132 13.9233 5.94379 14 7.33335V7.66669Z"
      stroke="currentColor"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface HeaderProps {
  className?: string;
}

interface DiscoverMenuItem {
  label: string;
  path: string;
}

const discoverMenuItems: DiscoverMenuItem[] = [
  { label: 'Stories', path: ROUTE_PATH.BROWSE_STORIES },
  { label: 'Peer Listeners', path: ROUTE_PATH.BROWSE_PEER_LISTENERS },
  { label: 'Resource Library', path: ROUTE_PATH.BROWSE_RESOURCES },
];

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0).toUpperCase() || '';
  const last = lastName?.charAt(0).toUpperCase() || '';
  return first + last || 'U';
};

interface MobileMenuItemProps {
  label: string;
  onClick: () => void;
  isBold?: boolean;
  showArrow?: boolean;
}

const MobileMenuItem: React.FC<MobileMenuItemProps> = ({ label, onClick, isBold = false, showArrow = true }) => (
  <button onClick={onClick} className="flex w-full items-center justify-between py-2.5">
    <span className={`font-open-sans text-xl leading-10 text-black ${isBold ? 'font-bold' : 'font-medium'}`}>
      {label}
    </span>
    {showArrow && <img src={ArrowRight} alt="" className="h-6 w-6" />}
  </button>
);

const Divider = () => <div className="bg-accent h-px w-full" />;

interface LogoProps {
  logoUrl: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

const Logo: React.FC<LogoProps> = ({ className = '', onClick, logoUrl, alt }) => (
  <div className={`h-[46px] w-[165px] ${onClick ? 'cursor-pointer' : ''} ${className}`} onClick={onClick}>
    <img src={logoUrl} alt={alt} className="h-full w-auto object-contain" style={{ imageRendering: 'auto' }} />
  </div>
);

export const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const { user, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const dfMdMedia = useMediaQuery('md');
  const { openModal } = useSettingsModal();
  const filteredDiscoverMenuItems = useMemo(() => discoverMenuItems, []);

  const [isDiscoverOpen, setIsDiscoverOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [callDetailsInitialSelectedCallId, setCallDetailsInitialSelectedCallId] = useState<number | undefined>(
    undefined,
  );
  const [callDetailsInitialSelectedActiveCallId, setCallDetailsInitialSelectedActiveCallId] = useState<
    number | undefined
  >(undefined);
  const [isHowCallWorksModalOpen, setIsHowCallWorksModalOpen] = useState(false);
  const [pendingReopenCallId, setPendingReopenCallId] = useState<number | undefined>(undefined);
  const [pendingReopenActiveCallId, setPendingReopenActiveCallId] = useState<number | undefined>(undefined);
  const [shouldReopenCallDetailsOnHowCallWorksClose, setShouldReopenCallDetailsOnHowCallWorksClose] = useState(false);
  const [isBrowseTopicsSubMenuOpen, setIsBrowseTopicsSubMenuOpen] = useState(false);
  const [isPostCallModalOpen, setIsPostCallModalOpen] = useState(false);
  const [openedPostCallId, setOpenedPostCallId] = useState<number | undefined>(undefined);
  const discoverRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);
  const avatarDropdownRef = useRef<HTMLDivElement>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);

  const { data: callRequests } = useCallRequests(user?.caller_role_id);
  const { data: topicTagGroups } = useTagGroups();
  const { data: incompletePostCalls } = usePostCalls(user?.caller_role_id, true);

  // Check if there are scheduled calls that might become active soon
  const hasUpcomingScheduledCalls = useMemo(() => {
    if (!callRequests) return false;
    return callRequests.some(
      (request) =>
        request.connections?.length > 0 &&
        (request.connections[0]?.status === 'accepted' || request.connections[0]?.status === 'offered') &&
        request.connections[0]?.scheduled_at !== null &&
        !request.is_backup_request,
    );
  }, [callRequests]);

  // Poll for active calls when there are scheduled calls that might become active
  const { data: activeCalls } = useActiveCalls(user?.caller_role_id, {
    refetchInterval: hasUpcomingScheduledCalls ? 5000 : undefined,
  });

  // Filter pending requests (matched/passed status or backup_pending, not scheduled)
  // Exclude passed calls where listener has acknowledged (call was passed on)
  const pendingRequests = useMemo(() => {
    if (!callRequests) return [];
    return callRequests.filter(
      (request) =>
        request.connections?.length > 0 &&
        (request.connections[0]?.status === 'matched' ||
          (request.connections[0]?.status === 'passed' && !request.connections[0]?.listener_acknowledged) ||
          request.status === 'backup_pending') &&
        request.connections[0]?.scheduled_at === null &&
        !request.is_backup_request,
    );
  }, [callRequests]);

  // Filter scheduled calls
  const scheduledCalls = useMemo(() => {
    if (!callRequests) return [];
    return callRequests
      .filter(
        (request) =>
          request.connections?.length > 0 &&
          (request.connections[0]?.status === 'accepted' || request.connections[0]?.status === 'offered'),
      )
      .filter((request) => request.connections[0]?.scheduled_at !== null)
      .filter((request) => !request.is_backup_request);
  }, [callRequests]);

  const hasScheduledCalls = scheduledCalls.length > 0;
  const hasActiveCalls = activeCalls && activeCalls.length > 0;
  const hasPendingRequests = pendingRequests.length > 0;
  const hasIncompletePostCalls = incompletePostCalls && incompletePostCalls.length > 0;
  const showUpcomingCallButton = hasScheduledCalls || hasActiveCalls || hasPendingRequests;

  // Priority: PostCall > UpcomingCall > CallAPeer
  const showPostCallButton = hasIncompletePostCalls && !showUpcomingCallButton;

  const poweredByHeaderEnabled = user?.caller_role?.active_subscription?.package?.client?.powered_by_header_enabled;
  const clientLogoUrl = poweredByHeaderEnabled
    ? user?.caller_role?.active_subscription?.package?.client?.powered_by_header_file_url
    : undefined;

  const logoSource = clientLogoUrl ?? domainLogo;
  const logoAlt = clientLogoUrl ? 'Client logo' : domainLogoAlt;

  const userInitials = useMemo(
    () => getInitials(user?.first_name, user?.last_name),
    [user?.first_name, user?.last_name],
  );
  const isHomeActive = useMemo(() => location.pathname === ROUTE_PATH.HOME, [location.pathname]);
  const isDiscoverActive = useMemo(
    () =>
      discoverMenuItems.some(
        (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
      ),
    [location.pathname],
  );

  // Close discover menu and avatar dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (discoverRef.current && !discoverRef.current.contains(target)) {
        setIsDiscoverOpen(false);
      }

      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(target)
      ) {
        setIsMobileMenuOpen(false);
      }

      if (
        isAvatarDropdownOpen &&
        avatarDropdownRef.current &&
        !avatarDropdownRef.current.contains(target) &&
        avatarButtonRef.current &&
        !avatarButtonRef.current.contains(target)
      ) {
        setIsAvatarDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, isAvatarDropdownOpen]);

  // Close mobile menu and submenu when navigating to a new page
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsBrowseTopicsSubMenuOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleDiscoverToggle = useCallback(() => {
    setIsDiscoverOpen((prev) => !prev);
  }, []);

  const handleMenuItemClick = useCallback(
    (path: string) => {
      setIsDiscoverOpen(false);
      history.push(path);
    },
    [history],
  );

  const handleHomeClick = useCallback(() => {
    history.push(ROUTE_PATH.HOME);
  }, [history]);

  const handleTalkNowClick = useCallback(() => {
    history.push(ROUTE_PATH.BROWSE_PEER_LISTENERS);
  }, [history]);

  const handleAvatarToggle = useCallback(() => {
    setIsAvatarDropdownOpen((prev) => !prev);
  }, []);

  const handleAvatarMenuItemClick = useCallback((action: () => void) => {
    action();
    setIsAvatarDropdownOpen(false);
  }, []);

  const handleMobileMenuToggle = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
    setIsBrowseTopicsSubMenuOpen(false);
  }, []);

  const handleBrowseTopicsBack = useCallback(() => {
    setIsBrowseTopicsSubMenuOpen(false);
  }, []);

  const handleBrowseTopicClick = useCallback(
    (tagGroupKey: string) => {
      history.push(`${ROUTE_PATH.BROWSE_TOPIC}/${tagGroupKey}`);
      setIsMobileMenuOpen(false);
      setIsBrowseTopicsSubMenuOpen(false);
    },
    [history],
  );

  const handleMobileMenuAction = useCallback(
    (action?: () => void, path?: string) => {
      if (path) {
        history.push(path);
      }
      if (action) {
        action();
      }
      setIsMobileMenuOpen(false);
    },
    [history],
  );

  const activeLinkClass =
    "after:absolute after:bottom-[-10px] after:left-0 after:block after:h-[3px] after:w-full after:rounded-full after:bg-icon-slate after:content-['']";

  if (!dfMdMedia) {
    return (
      <>
        <nav
          className={`fixed left-0 right-0 top-0 z-[60] flex h-[76px] items-center justify-between border-b ${
            isMobileMenuOpen ? 'border-input-border-gray' : 'border-button-secondary'
          } bg-white px-[30px] ${className}`}
        >
          {isBrowseTopicsSubMenuOpen ? (
            <button onClick={handleBrowseTopicsBack} className="flex h-9 w-9 items-center justify-center">
              <img src={ArrowRight} alt="Back" className="h-9 w-9 rotate-180" />
            </button>
          ) : (
            <Logo onClick={handleHomeClick} logoUrl={logoSource} alt={logoAlt} />
          )}
          {!isBrowseTopicsSubMenuOpen && (
            <div className="flex items-center gap-2">
              {showPostCallButton ? (
                <button
                  onClick={() => {
                    setOpenedPostCallId(incompletePostCalls?.[0]?.id);
                    setIsPostCallModalOpen(true);
                  }}
                  className="bg-connect-bg hover:bg-connect-bg-hover flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors"
                  aria-label="How'd it go?"
                >
                  <ChatBubbleIcon className="text-button-primary h-4 w-4" />
                </button>
              ) : showUpcomingCallButton ? (
                <button
                  onClick={() => setIsCallDetailsModalOpen(true)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-600 transition-colors hover:bg-green-700"
                  aria-label={hasActiveCalls ? 'Upcoming Call' : 'Call scheduled'}
                >
                  <img
                    src={CalendarIcon}
                    alt=""
                    className="h-[18px] w-[18px]"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                </button>
              ) : (
                <button
                  onClick={handleTalkNowClick}
                  className="bg-button-secondary text-button-primary hover:bg-button-secondary-hover flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors"
                  aria-label="Call a Listener"
                >
                  <CallAListenerIcon className="h-4 w-4" />
                </button>
              )}
              <button
                ref={hamburgerButtonRef}
                onClick={handleMobileMenuToggle}
                className="flex h-6 w-6 items-center justify-center"
              >
                <img
                  src={isMobileMenuOpen ? HamburgerClose : Hamburger}
                  alt={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                  className="h-6 w-6 transition-opacity"
                />
              </button>
            </div>
          )}
        </nav>

        {isMobileMenuOpen && (
          <div className="fixed inset-0 top-[76px] z-[50] bg-white" ref={mobileMenuRef}>
            <div
              className="flex h-[calc(100vh-130px)] flex-col overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {isBrowseTopicsSubMenuOpen ? (
                <div className="flex flex-col gap-6 px-[30px] pb-6 pt-6">
                  {topicTagGroups?.map((tagGroup, index) => (
                    <React.Fragment key={tagGroup.id}>
                      <MobileMenuItem
                        label={tagGroup.name}
                        onClick={() => handleBrowseTopicClick(tagGroup.key.toLowerCase())}
                        isBold
                      />
                      {index < (topicTagGroups?.length ?? 0) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-4 px-[30px] pb-24 pt-6">
                  {showPostCallButton ? (
                    <MobileMenuItem
                      label="How'd it go?"
                      onClick={() =>
                        handleMobileMenuAction(() => {
                          setOpenedPostCallId(incompletePostCalls?.[0]?.id);
                          setIsPostCallModalOpen(true);
                        })
                      }
                      isBold
                    />
                  ) : showUpcomingCallButton ? (
                    <MobileMenuItem
                      label="Upcoming Call"
                      onClick={() => handleMobileMenuAction(() => setIsCallDetailsModalOpen(true))}
                      isBold
                    />
                  ) : (
                    <MobileMenuItem
                      label="Call a Peer"
                      onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.BROWSE_PEER_LISTENERS)}
                      isBold
                    />
                  )}
                  <Divider />
                  <div className="flex flex-col gap-3">
                    <div className="py-2.5">
                      <span className="font-['Open_Sans'] text-[20px] font-bold leading-[40px] text-black">
                        Discover
                      </span>
                    </div>
                    {filteredDiscoverMenuItems.map((item) => (
                      <MobileMenuItem
                        key={item.path}
                        label={item.label}
                        onClick={() => handleMobileMenuAction(undefined, item.path)}
                      />
                    ))}
                  </div>
                  <Divider />
                  <MobileMenuItem label="Browse Topics" onClick={() => setIsBrowseTopicsSubMenuOpen(true)} isBold />
                  <Divider />
                  <div className="flex flex-col gap-3">
                    <div className="py-2.5">
                      <span className="font-['Open_Sans'] text-[20px] font-bold leading-[40px] text-black">
                        Settings
                      </span>
                    </div>
                    <MobileMenuItem
                      label="Profile"
                      onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.SETTINGS_PROFILE)}
                    />
                    <MobileMenuItem
                      label="Membership"
                      onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.SETTINGS_MEMBERSHIP)}
                    />
                    <MobileMenuItem
                      label="Info"
                      onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.SETTINGS_INFO)}
                    />
                  </div>
                  <Divider />
                  <MobileMenuItem
                    label="My Favorites"
                    onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.MY_FAVORITES)}
                  />
                  <MobileMenuItem
                    label="My Topic Preferences"
                    onClick={() => handleMobileMenuAction(undefined, ROUTE_PATH.TOPIC_PREFERENCES)}
                  />
                  <Divider />
                  <MobileMenuItem label="Sign Out" onClick={() => handleMobileMenuAction(logout)} showArrow={false} />
                </div>
              )}
            </div>
          </div>
        )}
        {isPostCallModalOpen && openedPostCallId && (
          <PostCallModal
            open={isPostCallModalOpen}
            onClose={() => {
              setIsPostCallModalOpen(false);
              setOpenedPostCallId(undefined);
            }}
            postCallId={openedPostCallId}
          />
        )}
        <CallDetailsModal
          open={isCallDetailsModalOpen}
          onClose={() => {
            setIsCallDetailsModalOpen(false);
            setCallDetailsInitialSelectedCallId(undefined);
            setCallDetailsInitialSelectedActiveCallId(undefined);
          }}
          callRequests={callRequests || []}
          initialSelectedCallId={callDetailsInitialSelectedCallId}
          initialSelectedActiveCallId={callDetailsInitialSelectedActiveCallId}
          onOpenHowCallWorks={(context) => {
            setPendingReopenCallId(context.selectedCallId);
            setPendingReopenActiveCallId(context.selectedActiveCallId);
            setShouldReopenCallDetailsOnHowCallWorksClose(true);
            setIsCallDetailsModalOpen(false);
            setIsHowCallWorksModalOpen(true);
          }}
        />
        <HowCallWorksModal
          open={isHowCallWorksModalOpen}
          onClose={() => {
            setIsHowCallWorksModalOpen(false);
            if (shouldReopenCallDetailsOnHowCallWorksClose) {
              setCallDetailsInitialSelectedCallId(pendingReopenCallId);
              setCallDetailsInitialSelectedActiveCallId(pendingReopenActiveCallId);
              setIsCallDetailsModalOpen(true);
              setShouldReopenCallDetailsOnHowCallWorksClose(false);
              setPendingReopenCallId(undefined);
              setPendingReopenActiveCallId(undefined);
            }
          }}
        />
      </>
    );
  }

  return (
    <nav
      className={`border-button-secondary flex h-[100px] items-center justify-center border-b bg-white px-0 py-6 ${className}`}
    >
      <div className="max-w-page flex w-full items-center justify-center gap-5 px-6">
        <div className="flex h-12 w-72 items-center px-0">
          <img
            src={logoSource}
            alt={logoAlt}
            className={`h-8 w-auto ${!isHomeActive ? 'cursor-pointer' : ''}`}
            style={{ imageRendering: 'auto' }}
            onClick={handleHomeClick}
          />
        </div>
        <div className="flex h-8 items-center gap-8">
          <button
            onClick={handleHomeClick}
            className={`font-albert-sans relative rounded-md px-0 py-0 text-xl leading-5 text-black transition-opacity hover:font-bold hover:opacity-80 ${
              isHomeActive ? 'font-bold' : 'font-semibold'
            } ${isHomeActive ? activeLinkClass : ''}`}
          >
            Home
          </button>
          <div ref={discoverRef} className="relative">
            <button
              onClick={handleDiscoverToggle}
              className={`font-albert-sans relative flex items-center gap-1.5 px-0 py-0 text-xl leading-5 text-black transition-opacity hover:opacity-80 ${
                isDiscoverActive ? 'font-bold' : 'font-semibold'
              } ${isDiscoverActive && !isDiscoverOpen ? activeLinkClass : ''}`}
            >
              Discover
            </button>
            {isDiscoverOpen && (
              <div className="border-input-border-gray absolute left-0 top-full z-50 mt-2 w-[343px] rounded-xl border bg-white p-2">
                <div className="flex flex-col gap-1.5">
                  {filteredDiscoverMenuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleMenuItemClick(item.path)}
                      className="hover:bg-input-border-gray group flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2 py-2 text-left transition-colors"
                    >
                      <span
                        className="font-open-sans group-hover:text-primary leading-3.5 text-body-gray text-sm font-medium transition-colors"
                        style={{ fontStyle: 'normal' }}
                      >
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-5">
          {showPostCallButton ? (
            <button
              onClick={() => {
                setOpenedPostCallId(incompletePostCalls?.[0]?.id);
                setIsPostCallModalOpen(true);
              }}
              className="bg-connect-bg hover:bg-connect-bg-hover flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 transition-colors"
            >
              <ChatBubbleIcon className="text-button-primary h-4 w-4" />
              <span className="font-albert-sans text-button-primary text-base font-medium leading-5">How'd it go?</span>
            </button>
          ) : showUpcomingCallButton ? (
            <button
              onClick={() => setIsCallDetailsModalOpen(true)}
              className="bg-button-secondary hover:bg-button-secondary-hover flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 transition-colors"
            >
              <img
                src={CalendarIcon}
                alt="Calendar"
                className="h-[22px] w-[22px] shrink-0"
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(48%) sepia(100%) saturate(2000%) hue-rotate(90deg) brightness(0.79) contrast(1.18)',
                }}
              />
              <span className="text-primary font-albert-sans shrink-0 text-base font-medium leading-5">
                {hasActiveCalls ? 'Upcoming Call' : 'Call scheduled'}
              </span>
            </button>
          ) : (
            <button
              onClick={handleTalkNowClick}
              className="bg-button-secondary hover:bg-button-secondary-hover flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 transition-colors"
            >
              <span className="text-button-primary">
                <CallAListenerIcon className="h-4 w-4 shrink-0" aria-hidden />
              </span>
              <span className="text-primary font-albert-sans text-base font-medium leading-5">Call a Listener</span>
            </button>
          )}
          <div ref={avatarDropdownRef} className="relative">
            <button
              ref={avatarButtonRef}
              onClick={handleAvatarToggle}
              type="button"
              className="group flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
            >
              <span className="relative flex h-10 w-10 items-center justify-center">
                <span
                  className="bg-profile-circle group-hover:bg-profile-circle-hover absolute inset-0 rounded-full transition-colors"
                  aria-hidden
                />
                <span className="font-albert-sans relative z-[1] text-xl font-medium leading-[18px] text-white">
                  {userInitials}
                </span>
              </span>
            </button>
            {isAvatarDropdownOpen && (
              <div className="border-input-border-gray absolute right-0 top-full z-50 mt-2 w-[200px] rounded-xl border bg-white p-2 shadow-lg">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleAvatarMenuItemClick(() => history.push(ROUTE_PATH.MY_FAVORITES))}
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="font-open-sans text-body-gray group-hover:text-primary text-sm font-medium leading-5 transition-colors">
                      My Favorites
                    </span>
                  </button>
                  <button
                    onClick={() => handleAvatarMenuItemClick(() => history.push(ROUTE_PATH.TOPIC_PREFERENCES))}
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="font-open-sans text-body-gray group-hover:text-primary text-sm font-medium leading-5 transition-colors">
                      My Topic Preferences
                    </span>
                  </button>
                  <div className="bg-input-border-gray my-1 h-px" />
                  <button
                    onClick={() =>
                      handleAvatarMenuItemClick(() => {
                        if (!dfMdMedia) {
                          history.push(ROUTE_PATH.SETTINGS_PROFILE);
                        } else {
                          openModal(SettingsPage.myProfile);
                        }
                      })
                    }
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="group-hover:text-primary font-open-sans text-body-gray text-sm font-medium leading-5 transition-colors">
                      Profile
                    </span>
                  </button>
                  <button
                    onClick={() =>
                      handleAvatarMenuItemClick(() => {
                        if (!dfMdMedia) {
                          history.push(ROUTE_PATH.SETTINGS_MEMBERSHIP);
                        } else {
                          openModal(SettingsPage.myPlan);
                        }
                      })
                    }
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="group-hover:text-primary font-open-sans text-body-gray text-sm font-medium leading-5 transition-colors">
                      My Plan
                    </span>
                  </button>
                  <button
                    onClick={() => handleAvatarMenuItemClick(() => setIsSupportModalOpen(true))}
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="group-hover:text-primary font-open-sans text-body-gray text-sm font-medium leading-5 transition-colors">
                      Support
                    </span>
                  </button>
                  <div className="bg-input-border-gray my-1 h-px" />
                  <button
                    onClick={() => handleAvatarMenuItemClick(() => setLogoutModalOpen(true))}
                    className="hover:bg-button-secondary group flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors"
                  >
                    <span className="group-hover:text-primary font-open-sans text-body-gray text-sm font-medium leading-5 transition-colors">
                      Log out
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <SupportModal
        open={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        onSuccessSubmitted={() => {
          Toast.success('Your support request has been successfully submitted', undefined);
        }}
      />
      <LogoutModal open={isLogoutModalOpen} onClose={() => setLogoutModalOpen(false)} />
      <CallDetailsModal
        open={isCallDetailsModalOpen}
        onClose={() => {
          setIsCallDetailsModalOpen(false);
          setCallDetailsInitialSelectedCallId(undefined);
          setCallDetailsInitialSelectedActiveCallId(undefined);
        }}
        callRequests={callRequests || []}
        initialSelectedCallId={callDetailsInitialSelectedCallId}
        initialSelectedActiveCallId={callDetailsInitialSelectedActiveCallId}
        onOpenHowCallWorks={(context) => {
          setPendingReopenCallId(context.selectedCallId);
          setPendingReopenActiveCallId(context.selectedActiveCallId);
          setShouldReopenCallDetailsOnHowCallWorksClose(true);
          setIsCallDetailsModalOpen(false);
          setIsHowCallWorksModalOpen(true);
        }}
      />
      <HowCallWorksModal
        open={isHowCallWorksModalOpen}
        onClose={() => {
          setIsHowCallWorksModalOpen(false);
          if (shouldReopenCallDetailsOnHowCallWorksClose) {
            setCallDetailsInitialSelectedCallId(pendingReopenCallId);
            setCallDetailsInitialSelectedActiveCallId(pendingReopenActiveCallId);
            setIsCallDetailsModalOpen(true);
            setShouldReopenCallDetailsOnHowCallWorksClose(false);
            setPendingReopenCallId(undefined);
            setPendingReopenActiveCallId(undefined);
          }
        }}
      />
      {isPostCallModalOpen && openedPostCallId && (
        <PostCallModal
          open={isPostCallModalOpen}
          onClose={() => {
            setIsPostCallModalOpen(false);
            setOpenedPostCallId(undefined);
          }}
          postCallId={openedPostCallId}
        />
      )}
    </nav>
  );
};
